import React, { useState } from 'react';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export interface MagicLinkAuthFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
  /**
   * URL the magic link should redirect to after the user clicks it.
   * Defaults to `${origin}/onboarding`.
   */
  emailRedirectTo?: string;
  /**
   * Optional pre-filled email (e.g. team invitation flow).
   */
  initialEmail?: string;
  /**
   * Optional override for the headline copy.
   */
  headline?: string;
  /**
   * Optional override for the subhead copy.
   */
  subhead?: string;
}

export const MagicLinkAuthForm: React.FC<MagicLinkAuthFormProps> = ({
  onCancel,
  onSuccess,
  emailRedirectTo,
  initialEmail = '',
  headline = 'Sign in to CommsGenie',
  subhead = "Enter your email and we'll send you a magic link. No password required.",
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const redirect =
        emailRedirectTo ??
        (typeof window !== 'undefined'
          ? `${window.location.origin}/onboarding`
          : undefined);

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: redirect ? { emailRedirectTo: redirect } : undefined,
      });

      if (otpError) throw otpError;

      setSentTo(email);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-black">Check your inbox</h3>
          <p className="text-sm text-gray-600">
            We sent a magic link to <span className="font-semibold">{sentTo}</span>.
            Click it to sign in — no password needed.
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
          Tip: if you don't see it, check your spam folder. The link expires in about an hour.
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="w-full rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="mb-2 text-xl font-semibold text-black">{headline}</h3>
        <p className="text-sm text-gray-600">{subhead}</p>
      </div>

      {error && (
        <div className="flex items-center space-x-2 rounded-lg border border-red-300 bg-red-50 p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-black placeholder-gray-500 transition-all duration-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="you@company.com"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg border border-green-600 bg-green-500 px-6 py-3 font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              <span>Sending link...</span>
            </div>
          ) : (
            'Send me a link'
          )}
        </button>
      </form>

      <div className="text-center">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Backwards-compatible alias so existing imports keep working.
export const AuthForm = MagicLinkAuthForm;
export type AuthFormProps = MagicLinkAuthFormProps;
